import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { LoginResponse, Login } from '../interfaces/login.interfaces';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.baseUrl}auth`;
  private router = inject(Router);
  private tokenKey = 'token';
  private userKey = 'user';
  private modulesKey = 'modules';

  constructor(private http: HttpClient) {}

  login(email: string, pass: string, remember: boolean, mfaCode?: string): Observable<LoginResponse> {
    const body: Login = { email, pass };
    if (mfaCode !== undefined) body.mfaCode = mfaCode;
    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, body).pipe(
      tap(response => {
        // A challenge is not a session, even if the response includes a token.
        if (this.requiresMfa(response)) return;
        if (typeof response.tkn !== 'string' || !response.tkn.trim()) {
          throw new Error('La respuesta de autenticación no contiene un token válido.');
        }
        const decodedToken: any = jwtDecode(response.tkn);

        const userInfo = {
          id: decodedToken.id,
          email: decodedToken.email,
          nombre: decodedToken.nombre || decodedToken.email,
          profile: {
            id: decodedToken.profile,
            nombre: this.getProfileName(decodedToken.profile)
          },
          company: decodedToken.company || null,
          modules: decodedToken.modules || [],           // (opcional, por compatibilidad)
          interactions: decodedToken.interactions || [], // ← NUEVO
          is_admin: decodedToken.is_admin || false,
          is_buho: decodedToken.is_buho || false,
          is_client: decodedToken.is_client || false
        };

        this.setToken(response.tkn, remember);
        this.setUser(userInfo, remember);               // guarda también las interacciones
        this.router.navigate(['/layout/dashboard']);
      })
    );
  }

  requiresMfa(response: LoginResponse): boolean {
    return response.mfa === 1 || response.mfa === '1';
  }

  getModules(): any[] {
    let modules = localStorage.getItem(this.modulesKey);
    if (!modules) {
      modules = sessionStorage.getItem(this.modulesKey);
    }
    return modules ? JSON.parse(modules) : [];
  }

  getModuleByName(moduleName: string): any {
    const modules = this.getModules();

    // Mapeo de nombres comunes a IDs de módulo (basado en tu lista)
    const moduleMap: { [key: string]: number } = {
      'sucursales': 7,
      'branches': 7,
      'campañas': 1,
      'encuestas': 2,
      'configuración': 3,
      'recursos': 4,
      'proyectos': 5,
      'proveedores': 6,
      'segmentación': 8
    };

    // Buscar por nombre exacto o similar
    const module = modules.find((mod: any) => {
      const modName = mod.nombre?.toLowerCase() || '';
      const searchName = moduleName.toLowerCase();

      return modName.includes(searchName) ||
            searchName.includes(modName) ||
            moduleMap[searchName] === mod.id;
    });

    // Si no encuentra, devolver uno por defecto basado en el nombre
    if (!module && moduleMap[moduleName.toLowerCase()]) {
      return {
        id: moduleMap[moduleName.toLowerCase()],
        nombre: moduleName
      };
    }

    return module;
  }

  getSucursalesModule(): any {
    const modules = this.getModules();
    // Intentar diferentes nombres comunes para el módulo de sucursales
    const possibleNames = ['sucursales', 'branches', 'sucursal', 'branch', 'tiendas', 'stores'];

    for (const name of possibleNames) {
      const module = modules.find((mod: any) =>
        mod.nombre?.toLowerCase().includes(name.toLowerCase())
      );
      if (module) return module;
    }

    // Si no encuentra, devolver el primer módulo o crear uno por defecto
    return modules.length > 0 ? modules[0] : { id: 1, nombre: 'sucursales' };
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    sessionStorage.removeItem(this.userKey);
    localStorage.removeItem(this.modulesKey);
    sessionStorage.removeItem(this.modulesKey);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey) || sessionStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  getUserInfo(): any {
    let user = localStorage.getItem(this.userKey);
    if (!user) {
      user = sessionStorage.getItem(this.userKey);
    }

    if (!user && this.getToken()) {
      const decodedToken: any = jwtDecode(this.getToken()!);
      return {
        id: decodedToken.id,
        email: decodedToken.email,
        nombre: decodedToken.nombre || decodedToken.email,
        profile: {
          id: decodedToken.profile,
          nombre: this.getProfileName(decodedToken.profile)
        },
        company: decodedToken.company || null,
        interactions: decodedToken.interactions || []  // ← NUEVO
      };
    }

    return user ? JSON.parse(user) : null;
  }

  getInteractions(): any[] {
    const user = this.getUserInfo();
    return user?.interactions || [];
  }

  /**
 * Retorna el id_modulo asociado a una clave de interacción.
 * Ejemplo: getModuleIdByClave('branches') → 7
 */
  getModuleIdByClave(clave: string): number | null {
    const interaction = this.getInteractions().find(i => i.clave === clave);
    return interaction ? interaction.id_modulo : null;
  }

  /**
 * Retorna el nombre del módulo asociado a una clave.
 */
  getModuleNameByClave(clave: string): string | null {
    const interaction = this.getInteractions().find(i => i.clave === clave);
    return interaction ? interaction.nombre : null;
  }

  getUserName(): string {
    const user = this.getUserInfo();
    return user ? user.nombre : 'Usuario';
  }

  private setToken(token: string, remember: boolean): void {
    (remember ? sessionStorage : localStorage).removeItem(this.tokenKey);
    if (remember) {
      localStorage.setItem(this.tokenKey, token);
    } else {
      sessionStorage.setItem(this.tokenKey, token);
    }
  }

  private setUser(user: any, remember: boolean): void {
    (remember ? sessionStorage : localStorage).removeItem(this.userKey);
    if (remember) {
      localStorage.setItem(this.userKey, JSON.stringify(user));
    } else {
      sessionStorage.setItem(this.userKey, JSON.stringify(user));
    }
  }

  public getProfileName(profileId: number): string {
    const profiles: { [key: number]: string } = {
      1: 'Buho',
      2: 'Usuario',
      3: 'Cliente'
    };
    return profiles[profileId] || 'Desconocido';
  }

  private setModules(modules: any[], remember: boolean): void {
    const modulesStr = JSON.stringify(modules);
    if (remember) {
      localStorage.setItem(this.modulesKey, modulesStr);
    } else {
      sessionStorage.setItem(this.modulesKey, modulesStr);
    }
  }
}
